import { m } from 'framer-motion'
import { Suspense, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router-dom'

import { TopStrip } from '@/features/landing/components/TopStrip'
import { RouteSkeleton } from '@/shared/components/RouteSkeleton'
import { markLeftFirstPage, skipEntranceAnimation } from '@/shared/lib/prerender'
import { pageVariants } from '@/shared/motion'

import { MobileTabBar } from './MobileTabBar'
import { SiteFooter } from './SiteFooter'
import { SiteHeader } from './SiteHeader'

/**
 * Layout principal. Renderiza el shell brutal global en TODAS las rutas:
 *
 * - `TopStrip` (marquee de stats) — solo en `/` (landing). Va por ENCIMA
 *   del SiteHeader para que sea lo primero que ve el usuario al cargar:
 *   banda negra con stats reales antes del navbar cream. En mobile scrollea
 *   con el contenido (no es sticky) — al hacer scroll desaparece y deja
 *   solo el sticky SiteHeader.
 * - `SiteHeader` brutal — auth-aware (CTAs login/register o UserMenu).
 * - `SiteFooter` brutal — solo en las rutas públicas/indexables:
 *   landing (`/`), listado de partidas (`/sessions`) y ayuda (`/help`).
 *   En las pantallas internas (sessions/new, sessions/mine, profile,
 *   sessions/:id, etc.) el footer se oculta para: (1) no competir con
 *   elementos sticky inferiores como la banda de live preview de
 *   `/sessions/new` o la `MobileTabBar`, y (2) seguir el patrón estándar
 *   de apps modernas (Linear, Notion, Figma).
 * - `MobileTabBar` sticky bottom — `md:hidden` interna.
 */
export function MainLayout() {
  const { pathname } = useLocation()
  // Primer render sobre HTML prerenderizado (o durante el prerender): sin animación de
  // entrada — ver shared/lib/prerender. Las navegaciones siguientes animan normal.
  const firstPath = useRef(pathname)
  useEffect(() => {
    if (pathname !== firstPath.current) markLeftFirstPage()
  }, [pathname])
  const { t } = useTranslation()
  // Match exacto — `/sessions/:id`, `/sessions/new` y `/sessions/mine` NO
  // llevan footer aunque empiezan por `/sessions`. Las legal pages son
  // landing-style públicas → sí llevan footer (con sus propios enlaces
  // cruzados a /terms, /privacy, etc., además del comportamiento esperado
  // de un sitio en producción).
  const PUBLIC_ROUTES = ['/', '/sessions', '/help', '/about', '/terms', '/privacy', '/contact']
  const showFooter = PUBLIC_ROUTES.includes(pathname)
  const isLanding = pathname === '/'

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Skip-link a11y — invisible hasta recibir focus */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-sm focus:bg-card focus:px-4 focus:py-2 focus:text-foreground focus:ring-2 focus:ring-blue"
      >
        {t('common.skipToContent')}
      </a>

      {isLanding && <TopStrip />}
      <SiteHeader />

      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        {/* Transición de página: fade + leve slide al cambiar de ruta. Keyed por
            pathname → re-dispara la entrada en cada navegación. Solo entrada (el
            data-router no conserva la ruta saliente para animar la salida).
            Bajo reduced-motion, MotionConfig omite el transform (queda el fade).
            El Suspense va DENTRO del m.div: si solo existiera el de SuspenseShell (por
            encima), una ruta con lazies anidados (p. ej. VenueLayout → VenueFichaPage)
            re-suspendía el árbol YA montado: React lo oculta y limpia sus efectos, la
            animación de entrada se cancelaba y la página se quedaba en opacity 0
            (invisible) al cargarla directamente. */}
        <m.div
          key={pathname}
          variants={pageVariants}
          initial={skipEntranceAnimation() && pathname === firstPath.current ? false : 'hidden'}
          animate="visible"
        >
          <Suspense fallback={<RouteSkeleton />}>
            <Outlet />
          </Suspense>
        </m.div>
      </main>

      {showFooter && <SiteFooter />}

      <MobileTabBar />
    </div>
  )
}
